import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useOutletContext } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import { normalizeLanguage } from "../../services/language";
import { setLanguage as setI18nLanguage, t, t as i18nT } from "../../i18n";

const normalizeModeContext = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized.includes("cienzar")) return "cienzar";
  if (normalized.includes("ciencia") || normalized.includes("free")) return "ciencia";
  if (normalized.includes("azar") || normalized.includes("dice")) return "azar";
  return normalized;
};

const inferPageContext = (pathname, fallback = "") => {
  const route = String(pathname || "").trim().toLowerCase();
  const fallbackValue = String(fallback || "").trim().toLowerCase();
  if (fallbackValue) return fallbackValue;
  if (!route || route === "/") return "lobby";
  if (route === "/game") return "lobby";
  if (route.startsWith("/game/support") || route.startsWith("/game/donate")) return "support";
  const gameRouteMatch = route.match(/^\/game\/([^/?#]+)/);
  if (!gameRouteMatch) return "game";
  const section = gameRouteMatch[1];
  const knownSections = new Set([
    "learn",
    "history",
    "stats",
    "support",
    "donate",
    "store",
    "friends",
    "spectate",
    "settings",
    "player",
  ]);
  if (!knownSections.has(section)) return "tournament";
  return section === "support" || section === "donate" ? "support" : "game";
};

const normalizeSupportMessage = (payload = {}) => {
  const id = String(payload?.id || payload?.messageId || "").trim();
  const userId = String(payload?.userId || payload?.senderId || "").trim().toLowerCase();
  const username = String(payload?.username || "User").trim() || "User";
  const text = String(payload?.message ?? payload?.text ?? "").trim();
  if (!text) return null;
  const parsedTimestamp =
    Number(payload?.timestamp) || new Date(payload?.createdAt || "").getTime();
  const timestamp =
    Number.isFinite(parsedTimestamp) && parsedTimestamp > 0
      ? parsedTimestamp
      : Date.now();
  const isAdmin = payload?.isAdmin === true;
  const page = String(payload?.page || "").trim().toLowerCase();
  const roomId = String(payload?.roomId || "").trim();
  const mode = normalizeModeContext(payload?.mode);
  const targetUserId = String(
    payload?.targetUserId || payload?.replyToUserId || payload?.recipientUserId || ""
  )
    .trim()
    .toLowerCase();
  const contactInfo = String(
    payload?.contactInfo || payload?.contact || payload?.phoneOrEmail || ""
  ).trim();
  const replyToMessageId = String(
    payload?.replyToMessageId || payload?.targetMessageId || ""
  ).trim();
  return {
    id,
    userId,
    username,
    text,
    timestamp,
    isAdmin,
    page,
    roomId,
    mode,
    targetUserId,
    contactInfo,
    replyToMessageId,
  };
};

const buildSupportMessageKey = (entry = {}) => {
  const directId = String(entry.id || "").trim();
  if (directId) return directId;
  return `${String(entry.username || "").trim().toLowerCase()}|${String(
    entry.text || ""
  ).trim()}|${Number(entry.timestamp) || 0}`;
};

const mergeSupportMessages = (primary = [], secondary = []) => {
  const seenKeys = new Set();
  const merged = [];
  [...primary, ...secondary].forEach((entry) => {
    const key = buildSupportMessageKey(entry);
    if (!key || seenKeys.has(key)) return;
    seenKeys.add(key);
    merged.push(entry);
  });
  merged.sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0));
  return merged;
};

const buildThreadedSupportMessages = (entries = []) => {
  const sortedEntries = [...entries].sort(
    (a, b) => (Number(a?.timestamp) || 0) - (Number(b?.timestamp) || 0)
  );
  const entriesById = new Map();
  sortedEntries.forEach((entry) => {
    const id = String(entry?.id || "").trim();
    if (!id) return;
    entriesById.set(id, entry);
  });

  const repliesByParentId = new Map();
  sortedEntries.forEach((entry) => {
    const parentId = String(entry?.replyToMessageId || "").trim();
    if (!parentId || entry?.isAdmin !== true || !entriesById.has(parentId)) return;
    const group = repliesByParentId.get(parentId) || [];
    group.push(entry);
    repliesByParentId.set(parentId, group);
  });
  repliesByParentId.forEach((group) => {
    group.sort((a, b) => (Number(a?.timestamp) || 0) - (Number(b?.timestamp) || 0));
  });

  const consumedReplyKeys = new Set();
  const threads = [];

  sortedEntries.forEach((entry) => {
    const parentId = String(entry?.replyToMessageId || "").trim();
    if (entry?.isAdmin === true && parentId && entriesById.has(parentId)) return;

    const entryId = String(entry?.id || "").trim();
    const replies = entryId
      ? (repliesByParentId.get(entryId) || []).filter((reply) => {
          const replyKey = buildSupportMessageKey(reply);
          if (!replyKey || consumedReplyKeys.has(replyKey)) return false;
          consumedReplyKeys.add(replyKey);
          return true;
        })
      : [];
    const hasPendingReply = entry?.isAdmin !== true && replies.length === 0;
    threads.push({
      entry,
      replies,
      hasPendingReply,
      isOrphanReply: false,
    });
  });

  sortedEntries.forEach((entry) => {
    const parentId = String(entry?.replyToMessageId || "").trim();
    if (!(entry?.isAdmin === true && parentId && !entriesById.has(parentId))) return;
    const entryKey = buildSupportMessageKey(entry);
    if (!entryKey || consumedReplyKeys.has(entryKey)) return;
    consumedReplyKeys.add(entryKey);
    threads.push({
      entry,
      replies: [],
      hasPendingReply: false,
      isOrphanReply: true,
    });
  });

  return threads;
};

const SUPPORT_EMAIL = "info@juegoxima.com";
const LEGACY_SUPPORT_EMAIL = "yosdelledsoy@gmail.com";

const supportDescription = {
  es: "Gracias por formar parte de nuestro maravilloso proyecto Xima. Si tienes alguna sugerencia o deseas enviarnos un mensaje, puedes escribirnos por WhatsApp al +1-864-753-6014 o +1-864-346-5333, o al correo info@juegoxima.com. También puedes dejarnos tu mensaje en este chat de soporte. Estaremos muy felices de responderte lo antes posible.",
  en: "Thank you for being part of our amazing Xima project. If you have any suggestions or would like to send us a message, you can contact us via WhatsApp at +1-864-753-6014 or +1-864-346-5333, or by email at info@juegoxima.com. You can also leave your message in this support chat. We will be happy to respond as soon as possible.",
  de: "Vielen Dank, dass Sie Teil unseres großartigen Xima-Projekts sind. Wenn Sie Vorschläge haben oder uns eine Nachricht senden möchten, können Sie uns über WhatsApp unter +1-864-753-6014 oder +1-864-346-5333 oder per E-Mail an info@juegoxima.com kontaktieren. Sie können Ihre Nachricht auch in diesem Support-Chat hinterlassen. Wir werden Ihnen so schnell wie möglich antworten.",
  ru: "Спасибо, что вы являетесь частью нашего замечательного проекта Xima. Если у вас есть предложения или вы хотите отправить нам сообщение, вы можете связаться с нами через WhatsApp по номерам +1-864-753-6014 или +1-864-346-5333, либо по электронной почте: info@juegoxima.com. Вы также можете оставить своё сообщение в этом чате поддержки. Мы будем рады ответить вам как можно скорее.",
};

const Donate = () => {
  const outletContext = useOutletContext();
  const location = useLocation();
  const { user } = useAuth();
  const language = normalizeLanguage(outletContext?.language);
  setI18nLanguage(language);
  const MAX_MESSAGE_LENGTH = 300;
  const [messages, setMessages] = useState([]);
  const [contactInput, setContactInput] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [highlightedMessageIds] = useState({});
  const [replyTarget, setReplyTarget] = useState(null);
  const messagesScrollRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const currentUserId = String(
    user?.loginIdentifier ||
      user?.userId ||
      user?.id ||
      user?.email ||
      user?.username ||
      user?.userName ||
      user?.name ||
      "guest"
  ).trim() || "guest";
  const currentUsername = String(
    user?.username || user?.userName || user?.name || user?.email || currentUserId
  ).trim() || "Guest";
  const normalizedCurrentUserId = currentUserId.toLowerCase();
  const normalizedCurrentUsername = currentUsername.toLowerCase();
  const isAdminUser = useMemo(() => {
    if (user?.isAdmin === true) return true;
    const roleCandidates = [
      user?.role,
      user?.type,
      user?.permission,
      user?.accountType,
      user?.accessLevel,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return roleCandidates.some((value) => value === "admin" || value === "superuser");
  }, [user?.accessLevel, user?.accountType, user?.isAdmin, user?.permission, user?.role, user?.type]);
  const currentPageContext = useMemo(
    () => inferPageContext(location?.pathname, outletContext?.page),
    [location?.pathname, outletContext?.page]
  );
  const currentRoomIdContext = useMemo(() => {
    const urlRoomId = new URLSearchParams(String(location?.search || "")).get("roomId");
    const contextRoomId = String(outletContext?.roomId || outletContext?.activeRoomId || "").trim();
    return String(contextRoomId || urlRoomId || "").trim();
  }, [location?.search, outletContext?.activeRoomId, outletContext?.roomId]);
  const currentModeContext = useMemo(() => {
    const urlMode = new URLSearchParams(String(location?.search || "")).get("mode");
    const contextMode = outletContext?.mode || outletContext?.gameMode || "";
    return normalizeModeContext(contextMode || urlMode || "");
  }, [location?.search, outletContext?.gameMode, outletContext?.mode]);
  const supportDescriptionText = useMemo(() => {
    const translatedDescription = t("supportDescription");
    if (translatedDescription && translatedDescription !== "supportDescription") {
      return translatedDescription;
    }
    return supportDescription[language] || supportDescription.es;
  }, [language]);
  const supportDescriptionContent = useMemo(() => {
    const normalizedText = String(supportDescriptionText || "")
      .replaceAll(LEGACY_SUPPORT_EMAIL, SUPPORT_EMAIL)
      .replace(
        new RegExp(`${SUPPORT_EMAIL}\\s*(?:y|and|und|и)\\s*${SUPPORT_EMAIL}`, "gi"),
        SUPPORT_EMAIL
      );
    const segments = normalizedText.split(SUPPORT_EMAIL);
    if (segments.length <= 1) return normalizedText;
    return segments.map((segment, index) => (
      <React.Fragment key={`support-description-segment-${index}`}>
        {index > 0 ? (
          <span
            className="mx-1 inline-block align-middle font-black text-[#40ff6a]"
            style={{ fontSize: "2em", lineHeight: 1 }}
          >
            {SUPPORT_EMAIL}
          </span>
        ) : null}
        {segment}
      </React.Fragment>
    ));
  }, [supportDescriptionText]);

  useEffect(() => {
    const messageListElement = messagesScrollRef.current;
    if (!messageListElement) return;
    if (shouldAutoScrollRef.current) {
      messageListElement.scrollTop = messageListElement.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const messageListElement = messagesScrollRef.current;
    if (!messageListElement) return undefined;

    const updateAutoScrollFlag = () => {
      const distanceToBottom =
        messageListElement.scrollHeight -
        messageListElement.scrollTop -
        messageListElement.clientHeight;
      shouldAutoScrollRef.current = distanceToBottom <= 80;
    };

    updateAutoScrollFlag();
    messageListElement.addEventListener("scroll", updateAutoScrollFlag);
    return () => {
      messageListElement.removeEventListener("scroll", updateAutoScrollFlag);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadSupportMessages = async () => {
      try {
        const response = await axios.get("/support/messages");
        const responseMessages = Array.isArray(response?.data?.messages)
          ? response.data.messages
          : [];
        const normalizedMessages = responseMessages
          .map((entry) => normalizeSupportMessage(entry))
          .filter(Boolean);
        if (!isMounted) return;
        setMessages(normalizedMessages);
        setIsHistoryLoaded(true);
      } catch (error) {
        if (!isMounted) return;
        setIsHistoryLoaded(true);
        setMessageError("Failed to load support messages.");
      }
    };

    loadSupportMessages();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSendMessage = async () => {
    if (isSending) return;
    const nextText = messageInput.trim();
    const targetUserId = isAdminUser ? String(replyTarget?.userId || "").trim() : "";
    const replyToMessageId = isAdminUser ? String(replyTarget?.messageId || "").trim() : "";
    const replyUsername = isAdminUser ? String(replyTarget?.username || "").trim() : "";
    const replyPrefix =
      targetUserId && replyUsername ? `Respuesta a ${replyUsername}: ` : "";
    const outgoingMessage =
      replyPrefix &&
      !nextText.toLowerCase().startsWith(replyPrefix.toLowerCase())
        ? `${replyPrefix}${nextText}`
        : nextText;

    if (!nextText) {
      setMessageError("Message cannot be empty.");
      return;
    }
    if (outgoingMessage.length > MAX_MESSAGE_LENGTH) {
      setMessageError(`Message must be ${MAX_MESSAGE_LENGTH} characters or less.`);
      return;
    }

    const contact = String(contactInput || "").trim();
    if (!contact && !isAdminUser) {
      setMessageError("Phone or email is required.");
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post("/support/messages", {
        contact,
        message: outgoingMessage,
        isAdmin: isAdminUser,
        page: currentPageContext,
        roomId: currentRoomIdContext,
        mode: currentModeContext,
        targetUserId,
        replyToMessageId,
      });
      const createdMessage = normalizeSupportMessage(response?.data?.message || {});
      if (createdMessage) {
        setMessages((previous) => mergeSupportMessages(previous, [createdMessage]));
      }
      setMessageError("");
      setMessageInput("");
      if (targetUserId) {
        setReplyTarget(null);
      }
    } catch (error) {
      const backendMessage = String(error?.response?.data?.message || "").trim();
      setMessageError(backendMessage || "Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageInputChange = (event) => {
    const nextValue = event.target.value;
    setMessageInput(nextValue);

    const trimmedValue = nextValue.trim();
    if (!trimmedValue && nextValue.length > 0) {
      setMessageError("Message cannot be empty.");
      return;
    }
    if (trimmedValue.length > MAX_MESSAGE_LENGTH) {
      setMessageError(`Message must be ${MAX_MESSAGE_LENGTH} characters or less.`);
      return;
    }
    setMessageError("");
  };

  const handleDeleteMessage = async (messageId, timestamp) => {
    if (!isAdminUser) return;
    const directId = String(messageId || "").trim();
    const fallbackId = messages.find(
      (entry) => Number(entry?.timestamp) === Number(timestamp || 0)
    )?.id;
    const targetId = String(directId || fallbackId || "").trim();
    if (!targetId) return;

    try {
      await axios.delete(`/support/messages/${encodeURIComponent(targetId)}`);
      setMessages((previous) =>
        previous.filter((entry) => String(entry?.id || "").trim() !== targetId)
      );
      setMessageError("");
    } catch (error) {
      const backendMessage = String(error?.response?.data?.message || "").trim();
      setMessageError(backendMessage || "Failed to delete message.");
    }
  };

  const handleClearAllMessages = async () => {
    if (!isAdminUser) return;
    const shouldClear =
      typeof window === "undefined"
        ? false
        : window.confirm(i18nT("common.areYouSureYouWantToClearAll"));
    if (!shouldClear) return;
    try {
      await axios.delete("/support/messages");
      setMessages([]);
      setReplyTarget(null);
      setMessageError("");
    } catch (error) {
      const backendMessage = String(error?.response?.data?.message || "").trim();
      setMessageError(backendMessage || "Failed to clear messages.");
    }
  };

  const handleSelectReplyTarget = (entry = {}) => {
    if (!isAdminUser) return;
    const targetUserId = String(entry?.userId || "").trim();
    if (!targetUserId) return;
    const targetUsername = String(entry?.username || "User").trim() || "User";
    const targetMessageId = String(entry?.id || "").trim();
    setReplyTarget((previous) => {
      if (String(previous?.userId || "").trim().toLowerCase() === targetUserId.toLowerCase()) {
        return null;
      }
      return {
        userId: targetUserId,
        username: targetUsername,
        messageId: targetMessageId,
      };
    });
  };

  const formatMessageTimestamp = (value) => {
    const numericTimestamp = Number(value);
    if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) return "";
    const date = new Date(numericTimestamp);
    if (Number.isNaN(date.getTime())) return "";
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    // Día de la semana y fecha, además de la hora (p. ej. «miércoles 15/10/2026 · 14:43»).
    const weekday = date.toLocaleDateString("es-ES", { weekday: "long" });
    const day = date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
    return `${weekday} ${day} · ${hours}:${minutes}`;
  };

  const inputLength = messageInput.length;
  const trimmedLength = messageInput.trim().length;
  const isCounterNearLimit = inputLength > 250;
  const isSendDisabled = trimmedLength === 0 || isSending;
  const formatContextLabel = (entry = {}) => {
    const page = String(entry?.page || "").trim();
    const mode = normalizeModeContext(entry?.mode);
    const roomId = String(entry?.roomId || "").trim();
    const parts = [];
    if (page) {
      parts.push(page.charAt(0).toUpperCase() + page.slice(1));
    }
    if (mode) {
      parts.push(mode.charAt(0).toUpperCase() + mode.slice(1));
    }
    if (roomId) {
      parts.push(`Room ${roomId}`);
    }
    if (parts.length === 0) return "General";
    return parts.join(" | ");
  };
  const visibleMessages = messages;

  return (
    <div className="h-full overflow-y-auto px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto w-full max-w-5xl rounded-3xl border border-cyan-400/50 bg-[linear-gradient(145deg,rgba(6,20,56,0.96),rgba(12,38,88,0.9))] p-6 shadow-[0_0_40px_rgba(34,211,238,0.16)] md:p-10">
        <div className="mb-8 flex items-center justify-center rounded-2xl border border-cyan-300/40 bg-black/25 p-4">
          <img src="/mejor logo 336.png" alt={t("LOGO_XIMA_ALT")} className="h-16 w-auto md:h-24" />
        </div>

        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold tracking-wide text-cyan-200 md:text-5xl">
              {t("support")}
            </h1>
          </div>

          <div className="space-y-4">
            <section className="rounded-2xl border border-cyan-300/30 bg-black/25 p-4 md:p-6">
              <h2 className="text-xl font-bold text-cyan-100">{t("contactUs")}</h2>
              <p className="mt-3 rounded-xl border border-cyan-300/30 bg-[#04112f]/45 p-3 text-sm leading-relaxed text-cyan-100/90">
                {supportDescriptionContent}
              </p>
              <div className="mt-4 space-y-3 rounded-xl border border-cyan-300/30 bg-[#04112f]/45 p-4">
                <h3 className="text-sm font-semibold text-cyan-100">{t("contactUs")}</h3>
                <p className="text-xs text-cyan-200/80">{`Messages (${visibleMessages.length})`}</p>
                <ul ref={messagesScrollRef} className="max-h-80 space-y-2 overflow-y-auto pr-1">
                  {isHistoryLoaded && visibleMessages.length === 0 ? (
                    <li className="rounded-xl border border-cyan-300/20 bg-[#04112f]/70 px-3 py-2 text-sm text-cyan-100/70">{i18nT("common.noMessagesYetBeTheFirstToWrite")}</li>
                  ) : null}
                  {buildThreadedSupportMessages(visibleMessages).map((thread, threadIndex) => {
                    const entry = thread?.entry || {};
                    const entryId = String(
                      entry.id || `${entry.userId || entry.username}-${entry.timestamp || 0}`
                    ).trim();
                    const entryUserId = String(entry.userId || "").trim().toLowerCase();
                    const entryUsername = String(entry.username || "").trim().toLowerCase();
                    const isAdminMessage = entry?.isAdmin === true;
                    const isHighlighted = Boolean(highlightedMessageIds[entryId]);
                    const isOwnMessage =
                      (entryUserId && entryUserId === normalizedCurrentUserId) ||
                      entryUsername === normalizedCurrentUsername;
                    const isReplyTargetMessage = Boolean(
                      isAdminUser &&
                        !isAdminMessage &&
                        String(replyTarget?.userId || "").trim().toLowerCase() === entryUserId
                    );
                    const showPending = thread?.hasPendingReply === true;

                    return (
                      <li
                        key={String(
                          entry.id || `${entry.username}-${entry.timestamp || 0}-${threadIndex}`
                        )}
                        className="space-y-2"
                      >
                        <div className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[88%] rounded-xl border px-3 py-2 ${
                              isReplyTargetMessage ? "ring-1 ring-cyan-300/70" : ""
                            } ${
                              isOwnMessage
                                ? "border-emerald-300/35 bg-emerald-600/20"
                                : isAdminMessage
                                  ? "border-amber-300/35 bg-amber-500/15"
                                  : "border-cyan-300/20 bg-[#04112f]/70"
                            } ${isHighlighted ? "shadow-[0_0_0_2px_rgba(34,211,238,0.55)] transition-shadow duration-700" : "transition-shadow duration-700"}`}
                          >
                            <p
                              className={`text-xs font-semibold ${
                                isOwnMessage
                                  ? "text-emerald-100"
                                  : isAdminMessage
                                    ? "text-amber-100"
                                    : "text-cyan-200"
                              }`}
                            >
                              {entry.username}
                              {isAdminMessage ? " (Admin)" : ""}
                            </p>
                            <p className="mt-1 break-words text-sm text-blue-100">{entry.text}</p>
                            {isAdminUser && !isAdminMessage && entry?.contactInfo ? (
                              <p className="mt-1 text-[11px] text-cyan-100/80">
                                {`Contacto: ${entry.contactInfo}`}
                              </p>
                            ) : null}
                            <p className="mt-1 text-[11px] text-cyan-200/55">
                              {formatContextLabel(entry)}
                            </p>
                            {showPending ? (
                              <p className="mt-1 text-[11px] font-semibold text-rose-300">{i18nT("common.pendiente")}</p>
                            ) : null}
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <p
                                className={`text-[11px] leading-none ${
                                  isOwnMessage
                                    ? "text-right text-emerald-100/70"
                                    : isAdminMessage
                                      ? "text-amber-100/70"
                                      : "text-cyan-200/60"
                                }`}
                              >
                                {formatMessageTimestamp(entry.timestamp)}
                              </p>
                              {isAdminUser ? (
                                <div className="flex items-center gap-1.5">
                                  {!isAdminMessage && entryUserId ? (
                                    <button
                                      type="button"
                                      onClick={() => handleSelectReplyTarget(entry)}
                                      className="rounded-md border border-cyan-300/40 px-2 py-0.5 text-[10px] font-semibold text-cyan-100 transition hover:bg-cyan-600/20"
                                    >
                                      {t("RESPONDER")}
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteMessage(entry.id, entry.timestamp)}
                                    className="rounded-md border border-rose-300/40 px-2 py-0.5 text-[10px] font-semibold text-rose-200 transition hover:bg-rose-600/20"
                                  >{i18nT("common.delete")}</button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {(Array.isArray(thread?.replies) ? thread.replies : []).map(
                          (replyEntry, replyIndex) => {
                            const replyId = String(
                              replyEntry.id ||
                                `${replyEntry.userId || replyEntry.username}-${replyEntry.timestamp || 0}`
                            ).trim();
                            const replyUserId = String(replyEntry.userId || "")
                              .trim()
                              .toLowerCase();
                            const replyUsername = String(replyEntry.username || "")
                              .trim()
                              .toLowerCase();
                            const isReplyAdminMessage = replyEntry?.isAdmin === true;
                            const isReplyHighlighted = Boolean(highlightedMessageIds[replyId]);
                            const isReplyOwnMessage =
                              (replyUserId && replyUserId === normalizedCurrentUserId) ||
                              replyUsername === normalizedCurrentUsername;

                            return (
                              <div
                                key={String(
                                  replyEntry.id ||
                                    `${replyEntry.username}-${replyEntry.timestamp || 0}-${threadIndex}-${replyIndex}`
                                )}
                                className={`pl-4 sm:pl-6 ${isReplyOwnMessage ? "flex justify-end" : "flex justify-start"}`}
                              >
                                <div
                                  className={`max-w-[88%] rounded-xl border px-3 py-2 ${
                                    isReplyOwnMessage
                                      ? "border-emerald-300/35 bg-emerald-600/20"
                                      : isReplyAdminMessage
                                        ? "border-amber-300/35 bg-amber-500/15"
                                        : "border-cyan-300/20 bg-[#04112f]/70"
                                  } ${isReplyHighlighted ? "shadow-[0_0_0_2px_rgba(34,211,238,0.55)] transition-shadow duration-700" : "transition-shadow duration-700"}`}
                                >
                                  <p
                                    className={`text-xs font-semibold ${
                                      isReplyOwnMessage
                                        ? "text-emerald-100"
                                        : isReplyAdminMessage
                                          ? "text-amber-100"
                                          : "text-cyan-200"
                                    }`}
                                  >
                                    {replyEntry.username}
                                    {isReplyAdminMessage ? " (Admin)" : ""}
                                  </p>
                                  <p className="mt-1 break-words text-sm text-blue-100">
                                    {replyEntry.text}
                                  </p>
                                  {isAdminUser &&
                                  !isReplyAdminMessage &&
                                  replyEntry?.contactInfo ? (
                                    <p className="mt-1 text-[11px] text-cyan-100/80">
                                      {`Contacto: ${replyEntry.contactInfo}`}
                                    </p>
                                  ) : null}
                                  <p className="mt-1 text-[11px] text-cyan-200/55">
                                    {formatContextLabel(replyEntry)}
                                  </p>
                                  <div className="mt-1 flex items-center justify-between gap-2">
                                    <p
                                      className={`text-[11px] leading-none ${
                                        isReplyOwnMessage
                                          ? "text-right text-emerald-100/70"
                                          : isReplyAdminMessage
                                            ? "text-amber-100/70"
                                            : "text-cyan-200/60"
                                      }`}
                                    >
                                      {formatMessageTimestamp(replyEntry.timestamp)}
                                    </p>
                                    {isAdminUser ? (
                                      <div className="flex items-center gap-1.5">
                                        {!isReplyAdminMessage && replyUserId ? (
                                          <button
                                            type="button"
                                            onClick={() => handleSelectReplyTarget(replyEntry)}
                                            className="rounded-md border border-cyan-300/40 px-2 py-0.5 text-[10px] font-semibold text-cyan-100 transition hover:bg-cyan-600/20"
                                          >
                                            {t("RESPONDER")}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDeleteMessage(replyEntry.id, replyEntry.timestamp)
                                          }
                                          className="rounded-md border border-rose-300/40 px-2 py-0.5 text-[10px] font-semibold text-rose-200 transition hover:bg-rose-600/20"
                                        >{i18nT("common.delete")}</button>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </li>
                    );
                  })}
                </ul>
                {isAdminUser ? (
                  <div className="rounded-lg border border-amber-300/35 bg-amber-500/10 p-3">
                    <h4 className="text-sm font-semibold text-amber-100">{i18nT("common.adminControls")}</h4>
                    <button
                      type="button"
                      onClick={handleClearAllMessages}
                      className="mt-2 inline-flex rounded-md border border-rose-300/50 px-3 py-1.5 text-xs font-semibold text-rose-200 transition hover:bg-rose-600/20"
                    >{i18nT("common.clearAllMessages")}</button>
                  </div>
                ) : null}
                {isAdminUser && replyTarget ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-cyan-300/35 bg-cyan-700/15 px-3 py-2">
                    <p className="text-xs text-cyan-100">
                      {`${t("RESPONDER")} ${replyTarget.username}`}
                    </p>
                    <button
                      type="button"
                      onClick={() => setReplyTarget(null)}
                      className="rounded-md border border-cyan-300/40 px-2 py-0.5 text-[10px] font-semibold text-cyan-100 transition hover:bg-cyan-700/25"
                    >
                      X
                    </button>
                  </div>
                ) : null}
                <label className="block text-sm font-semibold text-cyan-100" htmlFor="support-contact">
                  {t("phoneOrEmail")}
                </label>
                <input
                  id="support-contact"
                  type="text"
                  value={contactInput}
                  onChange={(event) => setContactInput(event.target.value)}
                  className="w-full rounded-xl border border-cyan-300/40 bg-[#04112f]/80 px-3 py-2 text-sm text-white outline-none placeholder:text-cyan-200/70 focus:border-cyan-300"
                  placeholder={t("phoneOrEmail")}
                />
                <label className="block text-sm font-semibold text-cyan-100" htmlFor="support-message">
                  {t("writeMessage")}
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    id="support-message"
                    type="text"
                    value={messageInput}
                    onChange={handleMessageInputChange}
                    className="w-full flex-1 rounded-xl border border-cyan-300/40 bg-[#04112f]/80 px-3 py-2 text-sm text-white outline-none placeholder:text-cyan-200/70 focus:border-cyan-300"
                    placeholder={t("writeMessage")}
                  />
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={isSendDisabled}
                    className="inline-flex justify-center rounded-xl border border-cyan-300/50 bg-cyan-600/20 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-600/30 disabled:cursor-not-allowed disabled:opacity-50 sm:whitespace-nowrap"
                  >
                    {t("ENVIAR_MENSAJE")}
                  </button>
                </div>
                <p
                  className={`text-right text-xs ${
                    isCounterNearLimit ? "text-amber-200" : "text-cyan-200/70"
                  }`}
                >
                  {`${inputLength}/${MAX_MESSAGE_LENGTH}`}
                </p>
                {messageError ? (
                  <p className="text-xs text-rose-300">{messageError}</p>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
